import { ArrowDownRight } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { HeroBackground } from "@/components/HeroBackground";
import { useBookConsultation } from "@/hooks/useBookConsultation";

const HEADLINE_LINES = [
  "Precision counsel for",
  "corporate structuring,",
  "compliance, and",
  "cross-border growth.",
];

export const Hero = () => {
  const bookConsultation = useBookConsultation();
  return (
    <section
      id="hero-section"
      data-testid={HERO.section}
      className="relative min-h-screen w-full flex items-end overflow-hidden bg-charcoal"
    >
      {/* Background video (muted loop, no controls) with static image fallback */}
      <HeroBackground />

      {/* Overlays for text contrast */}
      <div className="absolute inset-0 z-10 bg-charcoal/45" />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-charcoal via-charcoal/75 to-transparent" />

      {/* Content */}
      <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 lg:px-12 pb-12 md:pb-16">
        {/* Eyebrow */}
        <div className="fade-up flex items-center gap-4 mb-6" style={{ animationDelay: "0.2s" }}>
          <span className="h-px w-12 bg-gold" />
          <span className="font-sans text-[11px] tracking-[0.28em] uppercase text-gold">
            Middle East &amp; Africa Based Consultancy
          </span>
        </div>

        <div>
          {/* Masked line-by-line headline */}
          <h1
            data-testid={HERO.headline}
            className="font-serif text-canvas text-[21px] sm:text-[28px] lg:text-[42px] leading-[1.03] max-w-5xl"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden py-0.5">
                <span
                  className="line-reveal"
                  style={{ animationDelay: `${0.4 + i * 0.12}s` }}
                >
                  {line}
                </span>
              </span>
            ))}
          </h1>

          {/* Subhead + CTA */}
          <div
            className="fade-up mt-7 flex flex-col md:flex-row md:items-end md:justify-between gap-8"
            style={{ animationDelay: "1.3s" }}
          >
            <p
              data-testid={HERO.subhead}
              className="font-serif text-base md:text-lg text-canvas/75 leading-snug max-w-xl"
            >
              A Middle East and Africa based practice advising founders, boards,
              and investors across the UAE and Africa — from entity formation to
              complex cross-border transactions.
            </p>

            <button
              data-testid={HERO.cta}
              onClick={bookConsultation}
              className="group shrink-0 inline-flex items-center gap-3 font-sans text-xs tracking-[0.14em] uppercase font-bold px-8 py-4 bg-canvas text-charcoal border border-canvas transition-colors duration-300 hover:bg-forest hover:text-canvas hover:border-forest"
            >
              Book a Consultation
              <ArrowDownRight
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:translate-y-0.5"
                strokeWidth={1.5}
              />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
